import React from 'react'
import { cn } from '@/lib/utils'
import { logger } from '../../lib/logger'
import { useOnline } from '@/hooks/useOnline'
import { OFFLINE_SHORT_REASON } from './OfflineActions'

export type PastelTint = 'mint' | 'peach' | 'lavender' | 'butter' | 'blush' | 'sky'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  /** @deprecated — a pastel-filled button breaks the phase rule (pastels mean
   *  a phase of work, white is the action color). Kept so existing callers
   *  type-check; use 'primary' in new code. */
  | 'pastel'
  | 'danger'
  | 'icon'
  // @deprecated old variants map internally:
  | 'lime'
  | 'outline'
  | 'link'

export type ButtonSize = 'sm' | 'md' | 'hero'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize | 'lg'  // 'lg' is deprecated alias for 'hero'
  tint?: PastelTint
  leadingIcon?: React.ReactNode
  /** @deprecated use leadingIcon */
  leftIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  /** @deprecated use trailingIcon */
  rightIcon?: React.ReactNode
  loading?: boolean
  /** @deprecated use loading */
  isLoading?: boolean
  fullWidth?: boolean
  /**
   * The action writes (or starts a flow that writes): disabled while offline,
   * with the reason as its title (UX-018). See ui/OfflineActions.
   */
  needsNetwork?: boolean
}

const pastelBg: Record<PastelTint, string> = {
  mint:     'var(--mint)',
  peach:    'var(--peach)',
  lavender: 'var(--lavender)',
  butter:   'var(--butter)',
  blush:    'var(--blush)',
  sky:      'var(--sky)',
}

function resolveVariant(v: ButtonVariant | undefined): ButtonVariant {
  if (v === 'lime') {
    // Lime was the old primary accent; the primary is the accent pill now.
    if (import.meta.env.MODE !== 'production') {
      logger.warn('[Button] variant="lime" is deprecated — use variant="primary"')
    }
    return 'primary'
  }
  if (v === 'outline') {
    if (import.meta.env.MODE !== 'production') {
      logger.warn('[Button] variant="outline" is deprecated — use variant="secondary"')
    }
    return 'secondary'
  }
  if (v === 'link') {
    if (import.meta.env.MODE !== 'production') {
      logger.warn('[Button] variant="link" is deprecated — use variant="ghost"')
    }
    return 'ghost'
  }
  return v ?? 'primary'
}

function resolveSize(s: ButtonSize | 'lg' | undefined): ButtonSize {
  if (s === 'lg') {
    if (import.meta.env.MODE !== 'production') {
      logger.warn('[Button] size="lg" is deprecated — use size="hero"')
    }
    return 'hero'
  }
  return s ?? 'md'
}

const BASE =
  'inline-flex items-center justify-center font-semibold select-none whitespace-nowrap cursor-pointer ' +
  'rounded-[9999px] border-[0.5px] border-transparent ' +
  // Not transition-all: that also animated the focus ring, so right after a
  // Tab it was still half-drawn. The ring itself is the global :focus-visible
  // rule in index.css (white ring + black halo, UX-121) — no per-button ring.
  'transition-[background-color,border-color,color,opacity,transform,filter] duration-[250ms] ' +
  'active:scale-[0.96] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'

const VARIANT: Record<string, string> = {
  // Pressed: a light grey, never the 50% look of a disabled pill.
  primary:   'bg-[var(--accent)] text-[var(--accent-ink)] shadow-[0_8px_22px_-10px_var(--accent-glow)] hover:bg-[var(--accent-hover)] active:brightness-90',
  secondary: 'bg-white/[0.06] backdrop-blur-sm border-white/[0.10] text-[var(--midnight-text,color-mix(in srgb, rgb(var(--fg-rgb)) 91%, rgb(var(--bg-rgb))))] hover:bg-white/[0.10] hover:border-white/[0.18]',
  ghost:     'bg-transparent border-transparent text-[var(--midnight-text2,#94a3b8)] hover:text-[var(--midnight-text,color-mix(in srgb, rgb(var(--fg-rgb)) 91%, rgb(var(--bg-rgb))))] hover:bg-white/[0.04]',
  danger:    'bg-[var(--blush)]/10 border-[var(--blush)]/30 text-[var(--midnight-text,rgb(var(--fg-rgb)))] hover:bg-[var(--blush)]/20',
  icon:      'bg-white/[0.06] border-white/[0.10] text-[var(--midnight-text,color-mix(in srgb, rgb(var(--fg-rgb)) 91%, rgb(var(--bg-rgb))))] hover:bg-white/[0.10]',
  pastel:    'border-transparent text-black hover:brightness-105',
}

// sm is 36px with a mouse and 44px on touch (UX-013): the hit area is the
// button itself, so it can't be clipped by a scroll strip the way an
// invisible ::after hit area is.
const SIZE: Record<ButtonSize, string> = {
  sm:   'tap-target h-9 coarse:h-11 px-[14px] text-[13px] gap-[6px]',
  md:   'h-12 px-[22px] text-[15px] gap-2',
  hero: 'h-14 px-[28px] text-[17px] gap-2',
}

const ICON_SIZE: Record<ButtonSize, string> = {
  sm:   'tap-target h-9 w-9 coarse:h-11 coarse:w-11 p-0 text-[13px]',
  md:   'h-11 w-11 p-0 text-[15px]',
  hero: 'h-14 w-14 p-0 text-[17px]',
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant: variantProp = 'primary',
    size: sizeProp = 'md',
    tint,
    leadingIcon,
    leftIcon,
    trailingIcon,
    rightIcon,
    loading,
    isLoading,
    fullWidth = false,
    className,
    children,
    disabled,
    style,
    needsNetwork = false,
    title,
    ...props
  },
  ref,
) {
  const online = useOnline()
  const offlineBlocked = needsNetwork && !online
  const variant = resolveVariant(variantProp)
  const size = resolveSize(sizeProp)
  const spinning = loading ?? isLoading ?? false

  if (import.meta.env.MODE !== 'production' && variant === 'icon' && !props['aria-label'] && !props['aria-labelledby']) {
    logger.warn('[Button] variant="icon" used without aria-label or aria-labelledby — icon buttons must have an accessible label.')
  }
  const leading = leadingIcon ?? leftIcon
  const trailing = trailingIcon ?? rightIcon

  // Only the deprecated 'pastel' variant reads a tint ('lime' now resolves to
  // 'primary' upstream in resolveVariant).
  const resolvedTint: PastelTint =
    variant === 'pastel'
      ? (tint ?? 'mint')
      : 'mint'

  const inlineStyle: React.CSSProperties =
    variant === 'pastel'
      ? { background: pastelBg[resolvedTint], ...style }
      : (style ?? {})

  const sizeClass = variant === 'icon' ? ICON_SIZE[size] : SIZE[size]

  return (
    <button
      ref={ref}
      className={cn(
        BASE,
        VARIANT[variant],
        sizeClass,
        fullWidth && 'w-full',
        className,
      )}
      style={inlineStyle}
      disabled={disabled || spinning || offlineBlocked}
      title={offlineBlocked ? OFFLINE_SHORT_REASON : title}
      {...(needsNetwork ? { 'data-needs-network': '' } : {})}
      {...props}
    >
      {spinning ? <Spinner /> : leading}
      {children}
      {!spinning && trailing}
    </button>
  )
})

export default Button
